import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, PhoneIcon, MailIcon, ImageIcon } from 'lucide-react';
import { useLanguage } from '../lib/i18n';
import { getVehicleBySlug } from '../lib/vehicles';
interface VehicleDetailPageProps {
  vehicleSlug: string;
  onNavigateHome: () => void;
}
export function VehicleDetailPage({
  vehicleSlug,
  onNavigateHome
}: VehicleDetailPageProps) {
  const {
    t,
    lang
  } = useLanguage();
  const [selectedImage, setSelectedImage] = useState(0);
  const vehicle = getVehicleBySlug(vehicleSlug);
  if (!vehicle) {
    return <div className="min-h-screen bg-[#0a0a0a] pt-20 flex items-center justify-center" data-id="element-170">
        <div className="text-center" data-id="element-171">
          <p className="text-white text-xl mb-4" data-id="element-172">Vehicle not found</p>
          <button onClick={onNavigateHome} className="text-[#c8a96e] hover:text-[#d4b87a] transition-colors" data-id="element-173">
            {t('vehicles.backToHome')}
          </button>
        </div>
      </div>;
  }
  const handleContactClick = () => {
    onNavigateHome();
    setTimeout(() => {
      const element = document.querySelector('#kontakt');
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }, 100);
  };
  return <div className="min-h-screen bg-[#0a0a0a] pt-20" data-id="element-174">
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8" data-id="element-175">
        <motion.button initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.4
      }} onClick={onNavigateHome} className="flex items-center gap-2 text-[#a3a3a3] hover:text-white transition-colors" data-id="element-176">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-177" />
          {t('vehicles.backToHome')}
        </motion.button>
      </div>

      {/* Hero Image or Placeholder */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6
    }} className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-178">
        {vehicle.hasPhotos && vehicle.images.length > 0 ? <>
            {/* Main image */}
            <div className="aspect-[16/9] md:aspect-[21/9] rounded-xl overflow-hidden" data-id="element-179">
              <img src={vehicle.images[selectedImage]} alt={vehicle.name} className="w-full h-full object-cover object-center" data-id="element-180" />
            </div>

            {/* Thumbnail Gallery */}
            <div className="flex gap-4 mt-4 overflow-x-auto pb-2" data-id="element-181">
              {vehicle.images.map((img, index) => <button key={index} onClick={() => setSelectedImage(index)} className={`w-24 h-16 md:w-32 md:h-20 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0 ${selectedImage === index ? 'border-[#c8a96e]' : 'border-transparent opacity-60 hover:opacity-100'}`} data-id="element-182">
                  <img src={img} alt={`${vehicle.name} - ${index + 1}`} className="w-full h-full object-cover object-center" data-id="element-183" />
                </button>)}
            </div>
          </> : <div className="aspect-[16/9] md:aspect-[21/9] rounded-xl overflow-hidden bg-[#1c1c1c] border border-[#2a2a2a] flex flex-col items-center justify-center" data-id="element-184">
            <ImageIcon className="w-24 h-24 text-[#2a2a2a] mb-6" data-id="element-185" />
            <p className="text-[#737373] text-xl" data-id="element-186">
              {t('vehicleDetail.photosComingSoon')}
            </p>
          </div>}
      </motion.div>

      {/* Vehicle Info */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12" data-id="element-187">
        <div className="grid lg:grid-cols-3 gap-12 items-end" data-id="element-188">
          {/* Main Info */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="lg:col-span-2" data-id="element-189">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4" data-id="element-190">
              {vehicle.name}
            </h1>
            <p className="text-[#737373] mb-8" data-id="element-191">
              {t('vehicleDetail.vin')}: {vehicle.vin}
            </p>

            {/* Specifications */}
            <div className="bg-[#1c1c1c] border border-[#2a2a2a] rounded-xl p-6 md:p-8" data-id="element-192">
              <h2 className="text-xl font-semibold text-white mb-6" data-id="element-193">
                {t('vehicleDetail.specifications')}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6" data-id="element-194">
                <div data-id="element-195">
                  <p className="text-[#737373] text-sm mb-1" data-id="element-196">
                    {t('vehicleDetail.yearOfConstruction')}
                  </p>
                  <p className="text-white font-medium" data-id="element-197">{vehicle.year}</p>
                </div>
                <div data-id="element-198">
                  <p className="text-[#737373] text-sm mb-1" data-id="element-199">
                    {t('vehicleDetail.initialReg')}
                  </p>
                  <p className="text-white font-medium" data-id="element-200">
                    {lang === 'cs' ? vehicle.initialRegCs : vehicle.initialReg}
                  </p>
                </div>
                <div data-id="element-201">
                  <p className="text-[#737373] text-sm mb-1" data-id="element-202">
                    {t('vehicleDetail.model')}
                  </p>
                  <p className="text-white font-medium" data-id="element-203">{vehicle.model}</p>
                </div>
                <div data-id="element-204">
                  <p className="text-[#737373] text-sm mb-1" data-id="element-205">
                    {t('vehicleDetail.body')}
                  </p>
                  <p className="text-white font-medium" data-id="element-206">{vehicle.body}</p>
                </div>
                <div data-id="element-207">
                  <p className="text-[#737373] text-sm mb-1" data-id="element-208">
                    {t('vehicleDetail.mileage')}
                  </p>
                  <p className="text-white font-medium" data-id="element-209">{vehicle.mileage}</p>
                </div>
                <div data-id="element-210">
                  <p className="text-[#737373] text-sm mb-1" data-id="element-211">
                    {t('vehicleDetail.power')}
                  </p>
                  <p className="text-white font-medium" data-id="element-212">{vehicle.power}</p>
                </div>
                {vehicle.engineType && <div data-id="element-213">
                    <p className="text-[#737373] text-sm mb-1" data-id="element-214">
                      {t('vehicleDetail.engineType')}
                    </p>
                    <p className="text-white font-medium" data-id="element-215">
                      {lang === 'cs' ? vehicle.engineTypeCs : vehicle.engineType}
                    </p>
                  </div>}
                {vehicle.displacement && <div data-id="element-216">
                    <p className="text-[#737373] text-sm mb-1" data-id="element-217">
                      {t('vehicleDetail.displacement')}
                    </p>
                    <p className="text-white font-medium" data-id="element-218">
                      {vehicle.displacement}
                    </p>
                  </div>}
                {vehicle.torque && <div data-id="element-219">
                    <p className="text-[#737373] text-sm mb-1" data-id="element-220">
                      {t('vehicleDetail.torque')}
                    </p>
                    <p className="text-white font-medium" data-id="element-221">{vehicle.torque}</p>
                  </div>}
                <div data-id="element-222">
                  <p className="text-[#737373] text-sm mb-1" data-id="element-223">
                    {t('vehicleDetail.transmission')}
                  </p>
                  <p className="text-white font-medium" data-id="element-224">
                    {lang === 'cs' ? vehicle.transmissionCs : vehicle.transmission}
                  </p>
                </div>
                {vehicle.drive && <div data-id="element-225">
                    <p className="text-[#737373] text-sm mb-1" data-id="element-226">
                      {t('vehicleDetail.drive')}
                    </p>
                    <p className="text-white font-medium" data-id="element-227">
                      {lang === 'cs' ? vehicle.driveCs : vehicle.drive}
                    </p>
                  </div>}
                {vehicle.acceleration && <div data-id="element-228">
                    <p className="text-[#737373] text-sm mb-1" data-id="element-229">
                      {t('vehicleDetail.acceleration')}
                    </p>
                    <p className="text-white font-medium" data-id="element-230">
                      {vehicle.acceleration}
                    </p>
                  </div>}
                {vehicle.topSpeed && <div data-id="element-231">
                    <p className="text-[#737373] text-sm mb-1" data-id="element-232">
                      {t('vehicleDetail.topSpeed')}
                    </p>
                    <p className="text-white font-medium" data-id="element-233">{vehicle.topSpeed}</p>
                  </div>}
                {vehicle.weight && <div data-id="element-234">
                    <p className="text-[#737373] text-sm mb-1" data-id="element-235">
                      {t('vehicleDetail.weight')}
                    </p>
                    <p className="text-white font-medium" data-id="element-236">{vehicle.weight}</p>
                  </div>}
                {vehicle.wheels && <div data-id="element-237">
                    <p className="text-[#737373] text-sm mb-1" data-id="element-238">
                      {t('vehicleDetail.wheels')}
                    </p>
                    <p className="text-white font-medium" data-id="element-239">
                      {lang === 'cs' ? vehicle.wheelsCs : vehicle.wheels}
                    </p>
                  </div>}
                {vehicle.brakes && <div data-id="element-240">
                    <p className="text-[#737373] text-sm mb-1" data-id="element-241">
                      {t('vehicleDetail.brakes')}
                    </p>
                    <p className="text-white font-medium" data-id="element-242">
                      {lang === 'cs' ? vehicle.brakesCs : vehicle.brakes}
                    </p>
                  </div>}
              </div>
            </div>
          </motion.div>

          {/* Price & Contact Card — aligned to bottom of specs */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="self-end" data-id="element-243">
            <div className="bg-[#1c1c1c] border border-[#2a2a2a] rounded-xl p-6 md:p-8" data-id="element-244">
              <p className="text-[#737373] text-sm mb-2" data-id="element-245">
                {t('vehicleDetail.price')}
              </p>
              <p className="text-3xl md:text-4xl font-bold text-[#c8a96e] mb-2" data-id="element-246">
                {vehicle.price}
              </p>
              <p className="text-[#737373] text-sm mb-8" data-id="element-247">
                {t('vehicleDetail.inclVat')}
              </p>

              <button onClick={handleContactClick} className="w-full bg-[#c8a96e] hover:bg-[#d4b87a] text-[#0a0a0a] py-4 rounded-lg font-semibold transition-colors mb-6" data-id="element-248">
                {t('vehicleDetail.contactAbout')}
              </button>

              <div className="space-y-4 pt-6 border-t border-[#2a2a2a]" data-id="element-249">
                <a href="tel:+420603238888" className="flex items-center gap-3 text-[#a3a3a3] hover:text-[#c8a96e] transition-colors" data-id="element-250">
                  <PhoneIcon className="w-5 h-5" data-id="element-251" />
                  +420 603 238 888
                </a>
                <a href="mailto:info@porschetop.cz" className="flex items-center gap-3 text-[#a3a3a3] hover:text-[#c8a96e] transition-colors" data-id="element-252">
                  <MailIcon className="w-5 h-5" data-id="element-253" />
                  info@porschetop.cz
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>;
}